/**
 * A booking slot as the availability endpoint emits it, for ONE date.
 *
 * `scheduleSlotId` + `ticketIndex` are the server's own stamp of WHICH
 * configured ticket generated the slot (GetCourseAvailableSlots.controller.js
 * :700-744); they survive the response projection untouched.
 */
export interface AvailabilitySlotWindow {
  startMinutes?: number;
  endMinutes?: number;
  scheduleSlotId?: unknown;
  ticketIndex?: number;
}

/** A schedule slot as configured on the course. */
export interface ScheduleSlotConfig {
  _id?: unknown;
  tickets?: {
    _id?: unknown;
    duration?: number;
    ticketTypes?: string[];
  }[];
}

/**
 * A listing's ticket types, only as far as identity goes.
 *
 * ⚠ Deliberately carries no duration. Neither schema has one: the V1
 * `ticketType` model has no `duration` field at all, and the widget's V2
 * payload does not project one — which is why `pricingOptions`'
 * `?? tt.duration ?? 60` always lands on the 60.
 */
export interface TicketTypeLink {
  id?: string;
  _id?: string;
  ticketsIds?: string[];
}

/**
 * The ticket types a schedule-slot ticket belongs to.
 *
 * `ticket.ticketTypes` is the modern shape. A legacy V1 listing leaves it
 * empty and links the two the other way round — the ticket TYPE lists the slot
 * ticket's `_id` in its `ticketsIds` — which is the second arm
 * `appointment-time-slot-step.tsx:288` matches on. Without this, those
 * listings index nothing, every slot fails to match the chosen ticket, and no
 * window is ever shown for them.
 */
function ticketTypeIdsForSlotTicket(
  ticket: NonNullable<ScheduleSlotConfig["tickets"]>[number],
  ticketTypeLinks: TicketTypeLink[],
): string[] {
  // Stringified here too — see the note below. This arm returned the raw array,
  // which would key the index by object if the payload ever carried ObjectIds.
  const explicit = ticket?.ticketTypes || [];
  if (explicit.length > 0) return explicit.map(String);
  if (ticket?._id == null) return [];
  // ⚠ Stringified on EVERY side, like `slotBelongsToChosenTicket` — the course
  // payload has been seen with ObjectId and string ids both ways, and a raw
  // comparison anywhere in this chain makes the legacy link resolve nothing,
  // which is the exact failure it exists to prevent. That includes the ids
  // returned: they become `Map` keys and are compared to `chosenTicketTypeIds`.
  const slotTicketId = String(ticket._id);
  return ticketTypeLinks
    .filter((link) => {
      const linkId = link?.id ?? link?._id;
      return (
        (link?.ticketsIds || []).some((id) => String(id) === slotTicketId) ||
        (linkId != null && String(linkId) === slotTicketId)
      );
    })
    .map((link) => {
      const linkId = link?.id ?? link?._id;
      return linkId == null ? null : String(linkId);
    })
    .filter((id): id is string => !!id);
}

export interface AppointmentDurationInput {
  /** Every slot the SELECTED date offers at the selected start. */
  slotsAtSelectedStart: AvailabilitySlotWindow[];
  /**
   * The course's own schedule slots — `groupScheduleSlots` for a group
   * listing, `appointmentScheduleSlots` otherwise, keyed on `isGroupSchedule`
   * exactly as the server keys them.
   */
  scheduleSlots: ScheduleSlotConfig[];
  /**
   * Whether the availability call passed a `ticketTypeId`, which it does for
   * variable-duration listings only (api.ts). When it did, every slot returned
   * belongs to ONE ticket — `ticketSelections[0]`, not the whole basket — and
   * `ticketIndex` then indexes the FILTERED ticket array, so it must NOT be
   * used to look the ticket back up.
   */
  slotsAreTicketFiltered: boolean;
  /** Ticket types the customer has actually chosen for this booking. */
  chosenTicketTypeIds: string[];
  /**
   * ticketTypeId → configured duration, read RAW from the schedule slots.
   * Must not be `pricingOptions[].duration`: that is already mapped through
   * `?? tt.duration ?? 60`, so an unknown length arrives as a confident 60
   * and every "omit when unknowable" guard below becomes dead code.
   */
  durationByTicketType: Map<string, number>;
  /** Needed to resolve the legacy link — see `ticketTypeIdsForSlotTicket`. */
  ticketTypeLinks: TicketTypeLink[];
  /**
   * Ticket types configured with MORE THAN ONE length across the listing's
   * enabled slots — 30m Mon–Fri and 60m on Saturday, say. For those,
   * `durationByTicketType`'s single first-wins answer is arbitrary and must
   * not stand in for the per-slot value the server resolves.
   */
  ticketTypesWithVaryingDuration: Set<string>;
}

/**
 * ticketTypeId → its configured length, plus the ticket types the listing does
 * NOT give a single dependable length.
 *
 * The map is first-wins across every enabled slot, which is all a per-listing
 * index can be — the server resolves per slot per date. A ticket lands in
 * `ticketTypesWithVaryingDuration` when that first answer cannot speak for it:
 *
 * - two slots configure it with DIFFERENT lengths (30m Mon–Fri, 60m Saturday);
 * - or a slot sells it with **no** length at all. `duration` is optional on the
 *   slot schema with no default, and the server then falls back to
 *   `ticket.duration || course.duration || 60` — so a 90 recorded from another
 *   slot is that slot's length, not the ticket's.
 *
 * Both are built in one pass so they cannot drift apart.
 */
export function buildTicketDurationIndex(
  scheduleSlots: ScheduleSlotConfig[],
  /** Needed to resolve the legacy link — see `ticketTypeIdsForSlotTicket`. */
  ticketTypeLinks: TicketTypeLink[] = [],
  /**
   * The listing's own length, used for any slot ticket that configures none.
   * This mirrors the server exactly — it sizes each slot
   * `ticket.duration || course.duration || 60`
   * (GetCourseAvailableSlots.controller.js:700) — and leaving per-ticket
   * durations unset is a valid, common configuration. Without it every ticket
   * on such a listing looks unknowable and no window is ever shown.
   *
   * ⚠ The server's final `|| 60` is deliberately NOT mirrored: an invented
   * hour is the fabrication this whole module exists to refuse.
   */
  courseDurationMinutes?: number,
): {
  durationByTicketType: Map<string, number>;
  ticketTypesWithVaryingDuration: Set<string>;
} {
  const durationByTicketType = new Map<string, number>();
  const ticketTypesWithVaryingDuration = new Set<string>();

  for (const slot of scheduleSlots) {
    for (const ticket of slot?.tickets || []) {
      const duration = ticket?.duration || courseDurationMinutes;
      const usable = typeof duration === "number" && duration > 0;
      for (const ticketTypeId of ticketTypeIdsForSlotTicket(
        ticket,
        ticketTypeLinks,
      )) {
        if (!usable) {
          ticketTypesWithVaryingDuration.add(ticketTypeId);
          continue;
        }
        const seen = durationByTicketType.get(ticketTypeId);
        if (seen === undefined) durationByTicketType.set(ticketTypeId, duration);
        else if (seen !== duration)
          ticketTypesWithVaryingDuration.add(ticketTypeId);
      }
    }
  }

  return { durationByTicketType, ticketTypesWithVaryingDuration };
}

const lengthsOf = (slots: AvailabilitySlotWindow[]) =>
  new Set(
    slots
      .map((s) =>
        typeof s?.startMinutes === "number" && typeof s?.endMinutes === "number"
          ? s.endMinutes - s.startMinutes
          : Number.NaN,
      )
      .filter((d) => Number.isFinite(d) && d > 0),
  );

/** Did this slot come from a ticket the customer chose? */
function slotBelongsToChosenTicket(
  slot: AvailabilitySlotWindow,
  scheduleSlots: ScheduleSlotConfig[],
  chosenTicketTypeIds: string[],
  ticketTypeLinks: TicketTypeLink[],
): boolean {
  if (slot?.scheduleSlotId == null || typeof slot.ticketIndex !== "number") {
    return false;
  }
  // Stringified on both sides: the slot id is an ObjectId server-side and a
  // string once serialised, and the course payload has been seen both ways.
  const id = String(slot.scheduleSlotId);
  const config = scheduleSlots.find((s) => s?._id != null && String(s._id) === id);
  const ticket = config?.tickets?.[slot.ticketIndex];
  if (!ticket) return false;
  return ticketTypeIdsForSlotTicket(ticket, ticketTypeLinks).some((t) =>
    chosenTicketTypeIds.includes(t),
  );
}

/**
 * How long the chosen appointment actually runs, or null when it cannot be
 * known. Callers show the start alone rather than inventing an end.
 *
 * ORDER MATTERS.
 *
 * 1. **The chosen ticket's own slots on the selected DATE**, when they agree
 *    on one length. That is the server's per-date resolution, and it is the
 *    only source that can be right for a ticket configured 30m Mon–Fri and 60m
 *    on Saturday: `durationByTicketType` is first-wins across every schedule
 *    slot in the listing, so it pins such a ticket to whichever slot was
 *    declared first and disagrees with the server for half the week.
 *
 *    ⚠ The slots must be narrowed to the chosen ticket first. The server walks
 *    EVERY ticket in a schedule slot, stepping by that ticket's own duration,
 *    so a start can carry slots that belong to a DIFFERENT service — 09:30
 *    exists because a 30-minute trim steps there, while the 60-minute cut runs
 *    09:00/10:00/11:00. In fixed-duration mode the customer picks the time
 *    before the ticket, so they can land on exactly that start and then choose
 *    the cut; the lone 30-minute slot says nothing about it.
 *
 * 2. **Otherwise the ticket's configured length** — including when the start
 *    carries no slot of the chosen ticket at all, which is that same 09:30
 *    case.
 *
 *    ⚠ Several chosen ticket types of DIFFERENT lengths have no single window,
 *    so this reports none. (`pricingOptions` is sorted by price DESC, so a
 *    `.find()` over them would silently return the most expensive one's.)
 *
 * 3. **Otherwise every slot at the start**, when they agree — and ONLY while
 *    nothing is chosen. This is the fixed-duration timeslot step, which runs
 *    BEFORE the tickets step, so there is no ticket to narrow by yet. Once a
 *    ticket IS chosen the answer has to be about that ticket: the start's own
 *    slots belong to whichever services happen to step there.
 *
 *    ⚠ Never fall back to the auto-selected pricing option instead. Under
 *    `hidePricingOptions` the slot step selects one for us
 *    (`onOptionChange([...indices][0] ?? 0)`,
 *    appointment-time-slot-step.tsx:377), so it is never null once a time is
 *    picked and it is whichever slot the server happened to emit first.
 *
 * 4. Otherwise null — the listing offers several lengths at that start and
 *    nothing has been chosen, so there is no honest answer.
 */
export function resolveAppointmentDurationMinutes({
  slotsAtSelectedStart,
  scheduleSlots,
  slotsAreTicketFiltered,
  chosenTicketTypeIds,
  durationByTicketType,
  ticketTypeLinks,
  ticketTypesWithVaryingDuration,
}: AppointmentDurationInput): number | null {
  // ⚠ The map answers per LISTING; the server resolves per slot per date. Its
  // answer is only usable for a ticket that has a single configured length,
  // which is every ticket except the ones this set names.
  const mapCanAnswer = chosenTicketTypeIds.every(
    (id) => !ticketTypesWithVaryingDuration.has(id),
  );
  const chosenDurations = [
    ...new Set(
      chosenTicketTypeIds
        .map((id) => durationByTicketType.get(id))
        .filter((d): d is number => typeof d === "number" && d > 0),
    ),
  ];

  // Several services: only their configured lengths can answer, and only when
  // they agree. The slots cannot — a filtered response describes
  // `ticketSelections[0]` alone (appointmentCapTicketTypeId), and a start
  // carrying a slot for one of the chosen services says nothing about the
  // other. Two services of different lengths have no single window at all.
  if (chosenTicketTypeIds.length > 1) {
    // ⚠ Every one of them must have a configured length. `chosenDurations`
    // DROPS ids the map has no entry for, so without this check a 60-minute
    // service chosen alongside one of unknown length collapses to a confident
    // `[60]` — an absence reading as agreement. The server falls back to
    // `ticket.duration || course.duration || 60` for such a ticket, so its
    // real length is not knowable from here.
    const allKnown = chosenTicketTypeIds.every((id) =>
      durationByTicketType.has(id),
    );
    return allKnown && mapCanAnswer && chosenDurations.length === 1
      ? chosenDurations[0]!
      : null;
  }

  if (chosenTicketTypeIds.length === 1) {
    const ticketSlots = slotsAreTicketFiltered
      ? slotsAtSelectedStart
      : slotsAtSelectedStart.filter((s) =>
          slotBelongsToChosenTicket(
            s,
            scheduleSlots,
            chosenTicketTypeIds,
            ticketTypeLinks,
          ),
        );
    const ticketSlotLengths = lengthsOf(ticketSlots);
    if (ticketSlotLengths.size === 1) return [...ticketSlotLengths][0]!;
    // ⚠ Disagreeing slots for the CHOSEN ticket are the one signal that the
    // listing-wide map cannot be trusted, so they must not fall back to it.
    // Two enabled schedule slots on the same weekday can both sell the ticket
    // at the same start with different lengths; `durationByTicketType` is
    // first-wins and would answer with whichever was declared first, while the
    // server sizes the booking from whichever slot it matches. No answer is
    // honest here. An EMPTY set is different — the ticket simply does not step
    // to this start (the 09:30 case), and its configured length still stands.
    if (ticketSlotLengths.size > 1) return null;
    // Reached when the chosen ticket does not step to this start at all — the
    // 09:30 case. Its configured length still stands, but ONLY if the listing
    // configures one length for it.
    return mapCanAnswer && chosenDurations.length === 1
      ? chosenDurations[0]!
      : null;
  }

  const allLengths = lengthsOf(slotsAtSelectedStart);
  if (allLengths.size === 1) return [...allLengths][0]!;

  return null;
}
